import { DossierFilterDirectories } from '@/constant/dictionaries';
import { mapFirstLetterToUpper } from '@/lib/utils';
import { AppNotificationResponse } from '@/server/api/v1/notification/type';
import { SocketEndpoint } from '@/signalR/enum';
import { useSocketListener } from '@/signalR/use-socket-listener';
import { api } from '@/trpc/client';
import { DossierPageCodeFilter } from '@/types/enum';
import { useCallback, useEffect, useRef } from 'react';
import { useEmployeeInfoStore } from './use-employee-store';
import { useSidebarStore } from './use-sidebar-store';
import { useDossierStore } from '@/app/[locale]/(root)/(MC)/dossier/(processing-detail)/_components/utils/utils';
import { useDossierManagementStore } from '@/app/[locale]/(root)/(MC)/dossier/[dossier-status]/_components/utils';

const CHUNK_SIZE = 100 as const;

// Merge thành object {[path]: number}
const mergedObject = (data: Record<string, number>) => {
    return Object.keys(DossierFilterDirectories).reduce(
        (result, key) => {
            const keyMenuFilter = DossierFilterDirectories[key].keyMenuFilter;

            const count = data[keyMenuFilter];
            if (count) {
                result[`/dossier/${key}`] = count;
            }
            return result;
        },
        {} as Record<string, number>
    );
};

/**
 * Hàm thực hiện đếm số menu trên trang danh sách hồ sơ dựa theo số menu hiện tại của người dùng
 * @description Hàm này sẽ chỉ call sau khi server render xong ==> Mọi thứ đề thực hiện ở client
 *  */
export const useCountMenuFilter = (arrMenuPath: string[]) => {
    const apiUtils = api.useUtils();
    const empData = useEmployeeInfoStore((x) => x.current);
    const setObjCounter = useSidebarStore((x) => x.setObjCounter);
    const _socketTrigged = useRef<boolean>(false);

    const onSetObjCounterNumber = useCallback(
        (objCount: Record<string, number>) => {
            let mappedObjCount = mergedObject(objCount);

            setObjCounter((st) => ({
                ...st,
                ...mappedObjCount,
            }));
        },
        [setObjCounter]
    );

    const onHandleCounting = useCallback(
        (
            keys: DossierPageCodeFilter[],
            /**
             * Call đếm riêng lẻ cho từng filter
             */
            isCallAlone?: boolean
        ) => {
            if (keys.length > 0 && empData?.ID) {
                if (!isCallAlone) {
                    const func = apiUtils.dossier.Query_CountByFilter.fetch;

                    for (let i = 0; i <= keys.length; i += CHUNK_SIZE) {
                        // Gọi theo từng chunk 3 phần tử
                        const chunk = keys.slice(i, i + CHUNK_SIZE);

                        func({
                            employeeID: empData.ID,
                            pageCodes: chunk,
                        }).then((result) => {
                            if (result.isSuccessStatus && result.data) {
                                onSetObjCounterNumber(result.data);
                            }
                        });
                    }
                } else if (isCallAlone) {
                    const func = apiUtils.dossier.Query_CountEachFilter.fetch;

                    for (let key of keys) {
                        func({
                            employeeID: empData.ID,
                            pageCode: key,
                        }).then((result) => {
                            if (result.isSuccessStatus && !!result.data) {
                                onSetObjCounterNumber({
                                    [key]: result.data.Result,
                                });
                            }
                        });
                    }
                }
            }
        },
        [
            apiUtils.dossier.Query_CountByFilter.fetch,
            apiUtils.dossier.Query_CountEachFilter.fetch,
            empData?.ID,
            onSetObjCounterNumber,
        ]
    );

    /**Gọi hàm đếm số hồ sơ trên menu */
    const onDetectToCountMenuFilter = useCallback(
        async (menuOfUser: string[]) => {
            /**
             * Lấy ra các pageCodeFilter theo menu trạng thái hồ sơ
             */
            const arrFilterByDossierStatus: DossierPageCodeFilter[] = [];
            /**
             * Lấy ra các pageCodeFilter theo menu công việc hồ sơ
             */
            const arrFilterByDossierJob: DossierPageCodeFilter[] = [];

            const arrFilterJobSingle: DossierPageCodeFilter[] = [];

            Object.keys(DossierFilterDirectories).forEach((key) => {
                menuOfUser.forEach((menu) => {
                    if (menu.endsWith(key)) {
                        const _objMenuFilter = DossierFilterDirectories[key];

                        if (_objMenuFilter.isJob) {
                            if (
                                _objMenuFilter.pageCode ===
                                DossierPageCodeFilter.CV_DaNopChoTiepNhan
                            ) {
                                arrFilterJobSingle.push(
                                    _objMenuFilter.pageCode
                                );
                            } else {
                                arrFilterByDossierJob.push(
                                    _objMenuFilter.pageCode
                                );
                            }
                        } else {
                            arrFilterByDossierStatus.push(
                                _objMenuFilter.pageCode
                            );
                        }
                    }
                });
            });

            onHandleCounting(arrFilterJobSingle, true);
            onHandleCounting(arrFilterByDossierJob);
            onHandleCounting(arrFilterByDossierStatus);
        },
        [onHandleCounting]
    );

    const changedDossierIds: string[] | undefined = useDossierManagementStore((x) => x.changedDossierIds);
    const preDossierItemId = useRef<string | undefined>()
    /**Gọi đếm menu nếu có thông tin nhân viên */
    useEffect(() => {
        if (arrMenuPath?.length && empData?.ID) {
            const joinDossierIds = changedDossierIds && changedDossierIds.length > 0
                ? changedDossierIds?.join(',')
                : '';
            if (!preDossierItemId.current) {
                preDossierItemId.current = joinDossierIds;
                onDetectToCountMenuFilter(arrMenuPath);
                console.log(preDossierItemId.current);
            } else if (joinDossierIds.length > 0 && preDossierItemId.current != joinDossierIds) {
                preDossierItemId.current = joinDossierIds
                onDetectToCountMenuFilter(arrMenuPath);
                console.log({ joinDossierIds, preDossierItemId }, preDossierItemId.current);
            }
        }

        return () => {
            preDossierItemId.current = undefined
        }
    }, [arrMenuPath, onDetectToCountMenuFilter, empData?.ID, changedDossierIds]);

    const onReloadMenuFilter = useCallback(() => {
        if (!_socketTrigged.current) {
            apiUtils.dossier.Query_CountByFilter.invalidate();
            onDetectToCountMenuFilter(arrMenuPath);
            _socketTrigged.current = true;
        }
        setTimeout(
            () => {
                _socketTrigged.current = false; // Reset lại trigger
            },
            1000 * 60 * 3
        );
    }, [
        apiUtils.dossier.Query_CountByFilter,
        arrMenuPath,
        onDetectToCountMenuFilter,
    ]);

    const callbackListenSendNotification = useCallback(
        (data: AppNotificationResponse[]) => {
            data = mapFirstLetterToUpper(data);

            if (data.length) {
                for (let x of data) {
                    if (
                        ['DossierDetailProcesses', 'Dossiers'].includes(
                            x.EventName
                        )
                    ) {
                        onReloadMenuFilter();
                    }
                }
            }
        },
        [onReloadMenuFilter]
    );

    // Call lại hàm countMenuFilter nếu có thông báo mới
    useSocketListener({
        endPoint: SocketEndpoint.hubs,
        listener: {
            SendNotification: callbackListenSendNotification,
        },
    });
};
